import React, { useEffect, useRef, useState } from 'react';
import { continueSignInHere } from '../services/authService';
import { POPUP_HELP_AFTER_MS, signInTroubleCopy } from '../services/signInTrouble';

// Full-screen progress overlay for the sign-in flight. Blocks interaction
// (double-click on CTAs was possible during the silent gap) and names what
// is happening — the standard OAuth-handoff pattern.
//
// While Google's window is up it also offers a way out that keeps the
// sign-in (#3483): the overlay used to block the page with no exit, and on
// Mac Chrome students reloaded or went Back 2-4s after Google's window
// opened, which orphans that window. "Sign in in this tab" switches the same
// sign-in to the full-page redirect instead.
export default function SigningInOverlay({ phase }) {
    const [helpShown, setHelpShown] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [problem, setProblem] = useState(null);
    const startedAt = useRef(null);

    useEffect(() => {
        setHelpShown(false);
        setSwitching(false);
        setProblem(null);
        if (phase !== 'popup') return undefined;
        startedAt.current = Date.now();
        const timer = setTimeout(() => setHelpShown(true), POPUP_HELP_AFTER_MS);
        return () => clearTimeout(timer);
    }, [phase]);

    if (!phase) return null;

    const switchToTab = async () => {
        if (switching) return;
        setSwitching(true);
        setProblem(null);
        const error = await continueSignInHere({ waitedMs: Date.now() - (startedAt.current || Date.now()) });
        // Success navigates the page away; only a failure comes back here.
        if (error) {
            setProblem(signInTroubleCopy(error).message);
            setSwitching(false);
        }
    };

    const message = phase === 'popup'
        ? 'Waiting for Google sign-in…'
        : 'Signing you in — setting up your dashboard…';
    return (
        <div data-testid="signing-in-overlay"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1A4D2E]/40 backdrop-blur-sm">
            <div className="mx-4 max-w-md rounded-2xl bg-white px-8 py-6 shadow-2xl">
                <div className="flex items-center gap-4">
                    <span className="h-6 w-6 shrink-0 animate-spin rounded-full border-[3px] border-[#CFE0D2] border-t-[#1A4D2E]" />
                    <div>
                        <div className="font-semibold text-[#2C2C2C]">{message}</div>
                        <div className="mt-0.5 text-xs text-[#8A8A8A]">This can take a few seconds the first time.</div>
                    </div>
                </div>
                {phase === 'popup' && helpShown && (
                    <div data-testid="signin-popup-help" className="mt-4 border-t border-[#E8E2D6] pt-4" aria-live="polite">
                        <p className="text-sm text-[#4A4A4A]">
                            Don&apos;t see Google&apos;s window? It can open behind this one or on another screen.
                        </p>
                        <button
                            type="button"
                            onClick={switchToTab}
                            disabled={switching}
                            className="mt-3 min-h-[44px] w-full rounded-full bg-[#1A4D2E] px-5 text-sm font-semibold text-white hover:bg-[#2D6B45] disabled:opacity-60"
                        >
                            {switching ? 'Opening Google…' : 'Sign in in this tab'}
                        </button>
                        {problem && <p role="alert" className="mt-2 text-sm text-[#9B2C2C]">{problem}</p>}
                    </div>
                )}
            </div>
        </div>
    );
}
